/* eslint-disable react/jsx-no-undef */
import { useEffect, useMemo, useRef, useState } from "react";
import { db, functions } from "../../../config/firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  Timestamp,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import * as Headless from "@headlessui/react";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getDatabase, ref as rtdbRef, onValue, off } from "firebase/database";
import { Button, Dialog } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/20/solid";

// Catalyst components (your local components)
import { Select } from "../../../components/catalyst/select";
import { Field, Label } from "../../../components/catalyst/fieldset";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import rehypeRaw from "rehype-raw";
import MemberDetailsCard from "../../../components/MemberDetailsCard";
import { Combobox, ComboboxLabel, ComboboxOption } from "../../../components/catalyst/combobox";

type RelatedPerson = {
  id: string;
  name: string;
  relation: string;
};
type UserOption = {
  email: string
  id: string
  name: string
  relatedPersons?: RelatedPerson[]
};
type Version = "draft" | "published";
type TemplateOption = {
  key: string;
  id: string;
  version: Version;
  title: string;
  ts?: Timestamp | null;
  isRelated: boolean
};

const fmt = (ts?: Timestamp | { seconds: number; nanoseconds: number } | null) => {
  if (!ts) return "—";

  let d: Date;
  if (ts instanceof Timestamp) {
    d = ts.toDate();
  } else if (typeof (ts as any).seconds === "number") {
    d = new Date((ts as any).seconds * 1000);
  } else {
    return "—";
  }

  const DD = String(d.getDate()).padStart(2, "0");
  const MMM = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ][d.getMonth()];
  const YYYY = d.getFullYear();
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;

  return `${DD}-${MMM}-${YYYY}, ${h}:${m} ${ampm}`;
};

const firstTs = (o: any): Timestamp | null =>
  o?.publishedAt ?? o?.updatedAt ?? o?.createdAt ?? null;

const FUNCTIONS_REGION = "us-central1";

export default function TryItOut() {
  const [userId, setUserId] = useState<string>("");
  const [tplKey, setTplKey] = useState<string>("");

  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [tplOptions, setTplOptions] = useState<TemplateOption[]>([]);

  const [open, setOpen] = useState(false);
  const [openInstruction, setOpenInstruction] = useState(false);
  const [instructionContent, setInstructionContent] = useState<string>("");
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState<any | null>(null);
  const [layerTitles, setLayerTitles] = useState<string[]>([]);

  const [runId, setRunId] = useState<string | null>(null);
  const [runPayload, setRunPayload] = useState<any | null>(null);

  const unsubscribeRef = useRef<null | (() => void)>(null);
  const initFromRunRef = useRef<{ user: boolean; tpl: boolean }>({ user: false, tpl: false });
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [finalDone, setFinalDone] = useState(false);
  const isLocked = !!runId || starting;


  // relationship report
  const [showRelationDialog, setShowRelationDialog] = useState(false);
  const [selectedRelatedPerson, setSelectedRelatedPerson] = useState<string>("");
  const [selectedUserData, setSelectedUserData] = useState<UserOption | null>(null);


  const [showReport, setShowReport] = useState(false);

  const auth = getAuth();

  // load templates
  useEffect(() => {
    (async () => {
      try {
        setLoadingTemplates(true)
        const col = query(
          collection(db, "templates", "meta", "data"),
          orderBy("titleKey"),
          limit(500)
        );
        const parentSnap = await getDocs(col);

        const options: TemplateOption[] = await Promise.all(
          parentSnap.docs.map(async (d) => {
            const id = d.id;
            const draftRef = doc(d.ref, "versions", "draft");
            const pubRef = doc(d.ref, "versions", "published");
            const [ds, ps] = await Promise.all([getDoc(draftRef), getDoc(pubRef)]);

            const out: TemplateOption[] = [];
            if (ds.exists()) {
              const x = ds.data() as any;
              out.push({
                key: `${id}::draft`,
                id,
                version: "draft",
                title: (x.title as string) ?? d.get("title") ?? "(untitled)",
                ts: firstTs(x),
                isRelated: x.isRelationShipReport
              });
            }
            if (ps.exists()) {
              const x = ps.data() as any;
              out.push({
                key: `${id}::published`,
                id,
                version: "published",
                title: (x.title as string) ?? d.get("title") ?? "(untitled)",
                ts: firstTs(x),
                isRelated: x.isRelationShipReport
              });
            }
            return out;
          })
        ).then((rows) => rows.flat());

        options.sort((a, b) => {
          const t = a.title.localeCompare(b.title);
          if (t !== 0) return t;
          if (a.version === b.version) return 0;
          return a.version === "published" ? -1 : 1;
        });

        setTplOptions(options);
      } catch (e) {
        console.error("Failed to load templates", e);
      } finally {
        setLoadingTemplates(false)
      }
    })();
  }, []);
  useEffect(() => {
    if (!userId) return;
    if (!users.length) return;

    const found = users.find(u => u.id === userId);
    if (found) {
      setSelectedUserData(found);
    }
  }, [users, userId]);

  const selectedTpl = useMemo(() => {
    if (!tplKey) return null;
    const [id, version] = tplKey.split("::") as [string, Version];
    return { id, version };
  }, [tplKey]);

  const openView = async () => {
    if (!selectedTpl) return;
    setViewLoading(true);
    setViewData(null);
    setLayerTitles([]);
    try {
      const ref = doc(
        db,
        "templates",
        "meta",
        "data",
        selectedTpl.id,
        "versions",
        selectedTpl.version
      );
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setViewData(data);

        if (Array.isArray(data.layerIds) && data.layerIds.length > 0) {
          // fetch all layer titles
          const titles: string[] = [];
          for (const lid of data.layerIds) {
            try {
              const layerDoc = await getDoc(doc(db, "layers", "meta", "data", lid));
              if (layerDoc.exists()) {
                titles.push(layerDoc.get("title") || lid);
              } else {
                titles.push(lid); // fallback
              }
            } catch {
              titles.push(lid); // fallback
            }
          }
          setLayerTitles(titles);
        }
      } else {
        setViewData(null);
      }
      setOpen(true);
    } catch (e) {
      console.error("Failed to load template view", e);
    } finally {
      setViewLoading(false);
    }
  };
  useEffect(() => {
    if (!runPayload) return;

    const mainDone =
      runPayload?.statusLog?.executionResult?.status === "success";

    const annexuresNeeded = !!runPayload?.statusLog?.annexureBatchStart;
    const annexuresDone = !!runPayload?.statusLog?.annexureBatchDone;

    if (mainDone && (!annexuresNeeded || annexuresDone)) {
      setFinalDone(true);
    }
  }, [runPayload]);

  const handleTemplateSelect = async (key: string, userIdPresent:boolean = false) => {
    
    setTplKey(key);
    if(!userIdPresent){
      setUserId("");
    }
    setUsers([]);
  
    if (!key) return;
  
    const [templateId] = key.split("::");
    await fetchUsersForTemplate(templateId);
  };

  // subscribe to RTDB
  const subscribeRun = (rtdbId: string) => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    const dbRT = getDatabase();
    const node = rtdbRef(dbRT, `runs/${rtdbId}`);
    const handler = (snap: any) => {
      const val = snap.val();
      setRunPayload(val);
      console.log(val)
      if (
        !runId && // 🔒 IMPORTANT
        !initFromRunRef.current.user &&
        typeof val?.userId === "string" &&
        val.userId
      ) {
        console.log("here", val.userId)
        setUserId(val.userId);
        initFromRunRef.current.user = true;
      }
      
      if (
        !runId && // 🔒 IMPORTANT
        !initFromRunRef.current.tpl &&
        typeof val?.templateId === "string" &&
        val.templateId
      ) {
        const version: Version = val.isDraft ? "draft" : "published";
        setTimeout(async () => {
          const newKey = `${val.templateId}::${version}`;
          setTplKey(newKey);

          // 🔥 Trigger template user loading immediately
          await handleTemplateSelect(newKey, true);
        }, 3);

        initFromRunRef.current.tpl = true;
      }
    };
    onValue(node, handler);
    unsubscribeRef.current = () => off(node, "value", handler);
    setRunId(rtdbId);
  };

  const unsubscribeAndClear = async () => {
    const u = auth.currentUser;
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setRunId(null);
    setRunPayload(null);
    setShowReport(false);
    initFromRunRef.current = { user: false, tpl: false };
    if (u) {
      const latestRef = doc(db, "users", u.uid, "trialRun", "latest");
      await deleteDoc(latestRef).catch(() => { });
    }
  };

  // restore latest run pointer
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    (async () => {
      try {
        const latestRef = doc(db, "users", u.uid, "trialRun", "latest");
        const s = await getDoc(latestRef);
        if (s.exists()) {
          const d = s.data() as any;
          if (typeof d.rtdbId === "string" && d.rtdbId) {
            initFromRunRef.current = { user: false, tpl: false };
            subscribeRun(d.rtdbId);
          }
        }
      } catch (e) {
        console.error("Failed to restore latest run pointer", e);
      }
    })();
  }, [auth.currentUser?.uid]);

  const runWorkflow = async () => {
    const u = auth.currentUser;
    if (!u) return;

    try {
      setStarting(true);

      const callable = httpsCallable(functions, "workflowStart");
      const res: any = await callable({
        template: { templateId: selectedTpl?.id },
        userId: userId,
        relatedPersonId: selectedRelatedPerson || null,
        reportType: "trial",
        isDraft: selectedTpl?.version === "draft",
        userData: { relatedPersonId: selectedRelatedPerson || null }
      });

      const rtdbId = res?.data?.rtdbId;
      if (rtdbId) {
        const latestRef = doc(db, "users", u.uid, "trialRun", "latest");
        await setDoc(latestRef, { rtdbId }, { merge: true });
        subscribeRun(rtdbId);
      }

    } catch (e: any) {
      setError(e.message);
    } finally {
      setStarting(false);
      setShowRelationDialog(false);
    }
  }

  const startTrial = async () => {
    setError(null);

    if (!selectedTpl || !userId) {
      setError("Select a member and a template first.");
      return;
    }

    // 🔥 If the template is a relationship report, block and show dialog
    const templateMeta = tplOptions.find(t => t.key === tplKey);


    if (templateMeta?.isRelated) {
      if (!selectedUserData) {
        setError("Select a valid user.");
        return;
      }

      if (!selectedUserData.relatedPersons || selectedUserData.relatedPersons.length === 0) {
        setError("This user has no related persons.");
        return;
      }

      setShowRelationDialog(true);
      return; // ⛔ Stop normal flow
    }

    // otherwise → normal run
    runWorkflow();
  };

  const formattedReport = useMemo(() => {
    const txt = runPayload?.statusLog?.executionResult?.resultText ?? "";
    return txt
  }, [runPayload?.statusLog?.executionResult?.resultText]);

  const annexureReports = useMemo(() => {
    const annexObj =
      runPayload?.statusLog?.annexureBatchStart?.annexures || {};

    // Convert object → array
    return Object.entries(annexObj).map(([annexureId, data]: any) => ({
      annexureId,
      ...data,
    }));
  }, [runPayload?.statusLog?.annexureBatchStart?.annexures]);

  // Can start a run only when user & template selected AND not already subscribed
  const canRun = !!userId && !!tplKey && !runId;

  const { steps, allDone } = useMemo(
    () => buildSteps(runPayload?.statusLog),
    [runPayload?.statusLog]
  );

  const fetchUsersForTemplate = async (templateId: string) => {
    setLoadingUsers(true);
    try {
      const fetchUser = httpsCallable(functions, "templateManager");
      const res: any = await fetchUser({
        action: "filterUsersForTemplate",
        templateId,
      });
      setUsers(res.data?.users || []);
    } catch (err) {
      console.error("Failed to fetch template users", err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };




  return (
    <div className="mx-8 my-6">
      <h1 className="text-xl font-semibold text-zinc-700">Try it out</h1>

      <div className=" mt-8 space-y-6 flex justify-between">
        <div className="gap-5 w-[60%]">

          {/* Template */}
          <div className="flex justify-between items-end gap-3 mb-4  mt-4">
          <Field className="basis-[75%]">
  <Label>Select template</Label>

  <div className="relative inline-block w-[28rem]">
    <Combobox
      name="template"
      options={tplOptions}
      value={tplOptions.find(t => t.key === tplKey) || null}
      onChange={(tpl) => {
        if (!tpl) {
          setTplKey("");
          setUsers([]);
          return;
        }

        const newKey = tpl.key;
        handleTemplateSelect(newKey);
      }}
      displayValue={(tpl) => tpl ? `${tpl.title} — ${tpl.version}` : ""}
      placeholder="Select a template…"
      disabled={loadingTemplates || isLocked}
    >
      {(tpl) => (
        <ComboboxOption value={tpl}>
          <ComboboxLabel>
            {tpl.title} — {tpl.version}
          </ComboboxLabel>
        </ComboboxOption>
      )}
    </Combobox>

    {(loadingTemplates || isLocked) && (
      <div
        aria-hidden
        className="absolute rounded inset-0 z-10 cursor-not-allowed bg-gray-200/60"
      />
    )}
  </div>
</Field>





            <button
              onClick={openView}
              disabled={!selectedTpl}
              className={[
                "h-10 rounded-lg border text-sm, basis-[20%]",
                selectedTpl
                  ? "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                  : "border-zinc-200 text-zinc-400 cursor-not-allowed",
              ].join(" ")}
            >
              View Template
            </button>
          </div>


          {/* Member */}
          <Field>
            <Label>
              Select member <span className="text-rose-600">*</span>
            </Label>

            <div className="relative w-[28rem]">
              <Combobox
                 // IMPORTANT FIX
                value={users.find(u => u.id === userId) || null}
                onChange={(user) => {
                  setUserId(user?.id || "");
                  setSelectedUserData(user || null);
                }}
                options={users}
                displayValue={(u) => u?.name}
                placeholder={loadingUsers ? "Loading users…" : "Select a member…"}
                disabled={!tplKey || loadingUsers}
              >
                {(user) => (
                  <ComboboxOption key={user.id} value={user}>
                    <ComboboxLabel>{user.name}</ComboboxLabel>
                    <span className="ml-2 text-xs text-zinc-500">{user.email}</span>
                  </ComboboxOption>
                )}
              </Combobox>
              {( isLocked) && (
              <div className="absolute rounded  inset-0 z-10 cursor-not-allowed bg-gray-200/60" />
            )}
            </div>
          </Field>






          {/* Actions */}
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={startTrial}
              disabled={!canRun || starting}
              className={[
                "rounded-lg px-4 py-2 text-sm text-white",
                canRun && !starting ? "bg-brand-600 hover:bg-brand-600/90" : "bg-zinc-300 cursor-not-allowed",
              ].join(" ")}
            >
              {starting ? "Starting…" : "Run trial report"}
            </button>

            <button
              onClick={unsubscribeAndClear}
              disabled={!runId}
              className={[
                "rounded-lg px-6 py-2 text-sm font-medium",
                runId
                  ? "bg-brand-600 text-white hover:bg-brand-600/90"
                  : "bg-zinc-200 text-zinc-400 cursor-not-allowed",
              ].join(" ")}
            >
              Reset
            </button>
          </div>

          {error && <div className="text-sm text-rose-600">{error}</div>}

          {/* Stepper / Report */}
          {runPayload && (
            <div className="mt-6 rounded-lg border border-zinc-200 bg-white p-6">
              {!showReport ? (
                <>
                  <h3 className="mb-4 text-sm font-medium text-zinc-700">Execution Progress</h3>
                  <VerticalStepper steps={steps} />

                  {(allDone) && (
                    <div className="mt-6 rounded-lg border border-green-200 bg-green-50 p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="rounded-full bg-green-500 p-2">
                          <CheckIcon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-green-800">All Done</div>
                          <div className="text-sm text-green-700">
                            Workflow completed successfully.
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowReport(true)}
                        className="rounded-md bg-white px-3 py-1 text-sm font-medium text-green-700 shadow-sm hover:bg-gray-50"
                      >
                        View Report
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <div className="flex justify-between items-center my-2">
                    <h2 className="mb-4 text-m font-medium text-zinc-700">Report</h2>
                    <button
                      onClick={() => { setOpenInstruction(true); setInstructionContent(runPayload?.statusLog?.createLLMInstruction?.message || "No instruction message available") }}
                      disabled={!selectedTpl}
                      className={[
                        "h-10 rounded-lg border px-6 text-sm",
                        selectedTpl
                          ? "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                          : "border-zinc-200 text-zinc-400 cursor-not-allowed",
                      ].join(" ")}
                    >
                      View instruction
                    </button>
                  </div>

                  <div className="prose max-w-none text-sm text-gray-800 leading-relaxed  rounded-lg border bg-zinc-50 p-4 text-sm text-zinc-800  overflow-y-auto">
                    {/* <div 
                       className="markdown-preview mt-4"> */}
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                    >
                      {formattedReport}
                    </ReactMarkdown>



                    {/* </div> */}
                  </div>
                  {annexureReports.length > 0 && (
                    <div className="mt-8">
                      <h2 className="text-lg font-semibold text-zinc-800 mb-4">
                        Annexure Reports
                      </h2>

                      {annexureReports.map((ax) => (
                        <div
                          key={ax.annexureId}
                          className="mb-6 rounded-lg border bg-white p-4 shadow-sm"
                        >
                          <div className="flex justify-between items-center my-2">
                            <h3 className="text-md font-semibold text-zinc-700 mb-4">
                              {ax.title ?? `Annexure ${ax.annexureId}`}
                            </h3>
                            <button
                              onClick={() => {
                                setOpenInstruction(true); setInstructionContent(ax.inputText
                                  || "No instruction message available")
                              }}
                              disabled={!selectedTpl}
                              className={[
                                "h-10 rounded-lg border px-6 text-sm",
                                selectedTpl
                                  ? "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                                  : "border-zinc-200 text-zinc-400 cursor-not-allowed",
                              ].join(" ")}
                            >
                              View instruction
                            </button>
                          </div>


                          {/* Show the Annexure Output */}
                          <div className="prose max-w-none text-sm text-gray-800 leading-relaxed  rounded-lg border bg-zinc-50 p-4 text-sm text-zinc-800  overflow-y-auto">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeRaw, rehypeSanitize]}

                            >
                              {ax.output || "*No response generated*"}
                            </ReactMarkdown>
                          </div>


                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="w-[30%]">
          {(runPayload?.statusLog?.fetchUser?.data && showReport) && (

            <div className="self-start">
              <MemberDetailsCard data={runPayload?.statusLog?.fetchUser?.data} showMath={true} />
            </div>
          )}
        </div>

      </div>

      {/* VIEW DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-3xl rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <Dialog.Title className="text-lg font-medium text-zinc-700">
                Template preview {selectedTpl ? `(${selectedTpl.version})` : ""}
              </Dialog.Title>
              <button
                onClick={() => setOpen(false)}
                className="rounded-md px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5">
              {viewLoading ? (
                <div className="text-sm text-zinc-500">Loading…</div>
              ) : !viewData ? (
                <div className="text-sm text-zinc-500">No data.</div>
              ) : (
                <div className="space-y-5 max-h-[80vh] overflow-y-auto">
                  <div>
                    <div className="text-xs text-zinc-500">Title</div>
                    <div className="mt-0.5 text-zinc-900 font-medium">
                      {viewData.title ?? "(untitled)"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Short description</div>
                    <div className="mt-0.5 text-zinc-800">{viewData.shortDesc || "—"}</div>
                  </div>
                  <div className="flex gap-8">
                    <div>
                      <div className="text-xs text-zinc-500">Price</div>
                      <div className="mt-0.5 text-zinc-800">
                        {typeof viewData.price === "number"
                          ? `$${Number(viewData.price).toFixed(0)}`
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500">Last updated</div>
                      <div className="mt-0.5 text-zinc-800">{fmt(firstTs(viewData))}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">Layers</div>
                    <div className="mt-1 text-sm text-zinc-800">
                      {layerTitles.length > 0 ? layerTitles.join(", ") : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 text-sm font-medium text-zinc-700">Structure</div>
                    <div className="whitespace-pre-wrap rounded-lg border bg-zinc-50 p-3 text-sm text-zinc-800 ">
                      {typeof viewData.structure === "string"
                        ? viewData.structure
                        : JSON.stringify(viewData.structure ?? {}, null, 2)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* NEW INSTRUCTION DIALOG */}
      <Dialog
        open={openInstruction}
        onClose={() => setOpenInstruction(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <Dialog.Title className="text-lg font-medium text-zinc-700">
                Instruction Log
              </Dialog.Title>
              <button
                onClick={() => setOpenInstruction(false)}
                className="rounded-md px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5">
              <div className="whitespace-pre-wrap rounded-lg border bg-zinc-50 p-3 text-sm text-zinc-800 max-h-[80vh] overflow-y-auto">
                {instructionContent ??
                  "No instruction message available"}
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <Dialog open={showRelationDialog} onClose={() => {}} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">

            <h2 className="text-lg font-semibold text-zinc-800 mb-4">
              Select Related Person
            </h2>

            {selectedUserData?.relatedPersons?.length ? (
              <div className="space-y-3">
                {selectedUserData.relatedPersons.map((rp) => (
                  <label
                    key={rp.id}
                    className="flex items-center gap-3 cursor-pointer p-2 border rounded"
                  >
                    <input
                      type="radio"
                      name="relPerson"
                      value={rp.id}
                      checked={selectedRelatedPerson === rp.id}
                      onChange={() => setSelectedRelatedPerson(rp.id)}
                    />
                    <div>
                      <div className="font-medium">{rp.name}</div>
                      <div className="text-xs text-zinc-500">{rp.relation}</div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-sm text-red-600">
                This user has no related persons.
              </div>
            )}

            <div className="flex justify-end mt-6 gap-3">
              <button
                className="rounded bg-zinc-200 px-4 py-2 text-sm"
                onClick={() => setShowRelationDialog(false)}
                disabled={ starting}
              >
                Cancel
              </button>

              <button
                disabled={!selectedRelatedPerson || starting}
                className={[
                  "rounded px-4 py-2 text-sm text-white transition-colors",
                  starting
                    ? "bg-brand-400 cursor-wait"
                    : selectedRelatedPerson
                    ? "bg-brand-600 hover:bg-brand-600/90"
                    : "bg-zinc-300 cursor-not-allowed",
                ].join(" ")}
                onClick={runWorkflow}
              >
                {starting ? "Starting report…" : "Continue"}
              </button>
            </div>

          </Dialog.Panel>
        </div>
      </Dialog>


    </div>
  );
}

/* ------------------ Stepper Helpers ------------------ */
type StepStatus = "complete" | "current" | "upcoming" | "error";
type Step = { name: string; status: StepStatus; errorMessage?: string };

function buildSteps(statusLog: any): { steps: Step[]; allDone: boolean } {
  if (!statusLog) return { steps: [], allDone: false };

  const order = [
    { name: "Workflow Started", keys: ["workflowStarted"] },
    { name: "Fetch Data", keys: ["fetchTemplate", "fetchLayers", "fetchUser"] },
    { name: "Create LLM Instruction", keys: ["createLLMInstruction"] },
    { name: "Execute LLM Instruction", keys: ["executionResult"] },
  ];

  // --- FIXED LOGIC ---
  const annexObj = statusLog?.annexureBatchStart?.annexures;
  const hasAnnexures =
    annexObj && typeof annexObj === "object" && Object.keys(annexObj).length > 0;

  if (hasAnnexures) {
    order.push({
      name: "Executing Annexures",
      keys: ["annexureBatchStart", "annexureBatchDone"],
    });
  }
  // --------------------

  const norm = (log: any): StepStatus | undefined => {
    if (!log) return undefined;
    if (log.status === "success") return "complete";
    if (log.status === "error") return "error";
    if (log.state === "success" || log.state === "done") return "complete";
    return undefined;
  };

  let foundCurrent = false;

  const steps: Step[] = order.map((step) => {
    const logs = step.keys.map((k) => statusLog[k]).filter(Boolean);

    if (logs.some((l) => norm(l) === "error")) {
      return {
        name: step.name,
        status: "error",
        errorMessage: logs.find((l) => l?.error)?.error ?? "Step failed",
      };
    }

    if (hasAnnexures && step.name === "Executing Annexures") {
      const hasStart = !!statusLog.annexureBatchStart;
      const hasDone = !!statusLog.annexureBatchDone;

      if (hasStart && hasDone) return { name: step.name, status: "complete" };
      if (hasStart && !hasDone) {
        if (!foundCurrent) {
          foundCurrent = true;
          return { name: step.name, status: "current" };
        }
        return { name: step.name, status: "upcoming" };
      }
    }

    if (logs.length && logs.every((l) => norm(l) === "complete")) {
      return { name: step.name, status: "complete" };
    }

    if (!foundCurrent) {
      foundCurrent = true;
      return { name: step.name, status: "current" };
    }

    return { name: step.name, status: "upcoming" };
  });

  const mainDone =
    !!statusLog.executionResult &&
    statusLog.executionResult.status === "success";

  const annexuresDone = !!statusLog.annexureBatchDone;

  const allDone = mainDone && (!hasAnnexures || annexuresDone);

  return { steps, allDone };
}





function VerticalStepper({ steps }: { steps: Step[] }) {
  return (
    <nav aria-label="Progress">
      <ol role="list" className="overflow-hidden">
        {steps.map((step, stepIdx) => (
          <li
            key={step.name}
            className={stepIdx !== steps.length - 1 ? "pb-10 relative" : "relative"}
          >
            {step.status === "error" ? (
              <>
                {stepIdx !== steps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-red-500"
                  />
                )}
                <div className="relative flex items-start">
                  <span className="flex h-9 items-center">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white">
                      !
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-red-600">{step.name}</span>
                    {step.errorMessage && (
                      <span className="text-xs text-red-500">{step.errorMessage}</span>
                    )}
                  </span>
                </div>
              </>
            ) : step.status === "complete" ? (
              <>
                {stepIdx !== steps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-green-500"
                  />
                )}
                <div className="relative flex items-start">
                  <span className="flex h-9 items-center">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
                      <CheckIcon aria-hidden="true" className="h-5 w-5 text-white" />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-gray-900">{step.name}</span>
                  </span>
                </div>
              </>
            ) : step.status === "current" ? (
              <>
                {stepIdx !== steps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-300"
                  />
                )}
                <div className="relative flex items-start" aria-current="step">
                  <span className="flex h-9 items-center">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-green-500 bg-white">
                      <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-green-600">{step.name}</span>
                  </span>
                </div>
              </>
            ) : (
              <>
                {stepIdx !== steps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="absolute top-4 left-4 mt-0.5 -ml-px h-full w-0.5 bg-gray-300"
                  />
                )}
                <div className="relative flex items-start">
                  <span className="flex h-9 items-center">
                    <span className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white text-gray-400">
                      {stepIdx + 1}
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-gray-500">{step.name}</span>
                  </span>
                </div>
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

